import { useState, useEffect, useMemo, useCallback, Fragment, isValidElement, cloneElement, type ReactNode } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import { VStack } from '@components/base/layout';
import { Text } from '@components/base/display';
import { Alert, Spinner } from '@components/base/feedback';
import { RuleContextProvider } from '@components/shared/configurable';
import {
  ClaimSummaryCardConfigurable as ClaimSummaryCard,
  ClaimTasksCardConfigurable as ClaimTasksCard,
  CloseCoverageCardConfigurable as CloseCoverageCard,
  CloseTaskCardConfigurable as CloseTaskCard,
  ClotureSinistreCardConfigurable as ClotureSinistreCard,
  ComptabiliteCardConfigurable as ComptabiliteCard,
  CostSynthesisCardConfigurable as CostSynthesisCard,
  CreateTaskCardConfigurable as CreateTaskCard,
  DocumentsCardConfigurable as DocumentsCard,
  EditDocumentCardConfigurable as EditDocumentCard,
  EditIntervenantCardConfigurable as EditIntervenantCard,
  EmailCardConfigurable as EmailCard,
  EstimationCardConfigurable as EstimationCard,
  EstimationRecoursCardConfigurable as EstimationRecoursCard,
  IntervenantFormConfigurable as IntervenantForm,
  IntervenantsCardConfigurable as IntervenantsCard,
  MiseAJourDossierCardConfigurable as MiseAJourDossierCard,
  MissingPiecesCardConfigurable as MissingPiecesCard,
  OpenCoverageCardConfigurable as OpenCoverageCard,
  OtherActionsPanelConfigurable as OtherActionsPanel,
  OuvertureSinistreCardConfigurable as OuvertureSinistreCard,
  RecoveryCardConfigurable as RecoveryCard,
  RequestPiecesCardConfigurable as RequestPiecesCard,
  SettlementCardConfigurable as SettlementCard,
} from '../components/register';
import { CommentsPanel } from '../components/CommentsPanel';
import { TaskTitle } from '../components/TaskTitle';
import { AIChat } from '../components/AIChat';
import { useAiChatStore } from '../components/aiChatStore';
import { useActionInjection } from '../components/useActionInjection';
import { useClaimDataStore } from '../components/claimDataStore';
import { usePopoutEmailReplyListener } from '../components/documents/documentsPopout';
import { OTHER_ACTIONS } from '../configs/otherActionsConfig';
import { taskService } from '../api/taskService';
import type { VeosCostLine, VeosClaimSummary, VeosGuaranteeToClose, VeosGuaranteeToOpen, VeosTaskInfo } from '../api/taskService';
import type { CostLine, ClaimSummary, ProvisionToRecoverRow } from '../types';
import { useClaimFlowAuthStore } from '../auth/store';
import { buildTaskDetailRuleContext } from '../contextVariables';

function getTypeLabel(info: VeosTaskInfo): string {
  if (info.ECRAN?.toUpperCase() === 'AUTRE') return 'Action libre';
  return info.CLASSEMENT_LIB ?? '';
}

/** Page title texts (stable keys). */
const PAGE_TITLE_TEXT = {
  task: 'Tâche',
  claim: 'Dossier',
  separator: ' — ',
} as const;

/**
 * "Tâche {id} — {type} — {classement}" on a task; "Dossier {NCBT_NCIE}" on a
 * claim opened without a task (claim search, mahhp 22861).
 */
function buildPageTitle(
  taskId: string | undefined,
  taskInfo: VeosTaskInfo | null,
  dossierNumber: string | undefined,
): string {
  if (!taskId) {
    return dossierNumber ? `${PAGE_TITLE_TEXT.claim} ${dossierNumber}` : PAGE_TITLE_TEXT.claim;
  }
  return [
    `${PAGE_TITLE_TEXT.task} ${taskId}`,
    taskInfo ? getTypeLabel(taskInfo) : '',
    taskInfo?.CLASSEMENT ?? '',
  ]
    .filter((part) => part !== '')
    .join(PAGE_TITLE_TEXT.separator);
}

/** `CD_PSD` of a garantie's main row in `WS2_GS_TAB_SINGAR` (mahhp 22854). */
const COST_TOTAL_CD_PSD = 'TOTAL';

/**
 * Whether a `WS2_GS_TAB_SINGAR` row is a garantie's main row (`CD_PSD ===
 * 'TOTAL'`) rather than one of its poste detail rows. A row carrying no
 * `CD_PSD` at all (a pre-22854 payload, which has no poste rows) is a main
 * row too, so such a payload keeps rendering — and summing — as before.
 */
function isGuaranteeMainRow(cdPsd: string | null | undefined): boolean {
  return cdPsd === undefined || cdPsd === null || cdPsd === '' || cdPsd === COST_TOTAL_CD_PSD;
}

/**
 * Maps every SINGAR row (main + poste rows, API order) for the cost synthesis
 * card. Consumers needing the claim-level figures (the `totalCost` total, the
 * rule context) must keep to the `isTotal` rows — poste rows break a garantie
 * down, so adding them would double-count it.
 */
function mapCostLines(erpLines: VeosCostLine[]): CostLine[] {
  return erpLines.map((line) => ({
    guarantee: line.LIB_GAR,
    provision: line.PROV_RESTANTE,
    settlement: line.REG,
    grossCharge: line.PROV,
    forecast: line.RECPROV,
    recourse: line.RECREG,
    cost: line.COUT_CIE,
    isTotal: isGuaranteeMainRow(line.CD_PSD),
    poste: line.LIB_PSD,
  }));
}

function mapClaimSummary(raw: VeosClaimSummary): ClaimSummary {
  return {
    client: raw.PASS_NOM_PER,
    dossierNumber: raw.NCBT_NCIE,
    date: new Date(raw.DATS_SIN).toISOString().slice(0, 10),
    openingDate: new Date(raw.DATO_SIN).toISOString().slice(0, 10),
    status: raw.LIB_MVT,
    materialManager: raw.GESTIONNAIRE ?? '-',
    bodilyManager: raw.GESTCORPO ?? '-',
    plate: raw.IMMAT_VEH ?? '-',
    location: raw.LIEU,
    responsibility: `${String(raw.TXRESP_SIN)}%`,
    type: raw.CB_GESTION_1ET2 ? raw.CB_GESTION_1ET2.replace('>', ' > ') : '-',
    article: raw.LIB_IDA ?? '-',
    collisionType: raw.LIB_CIRC,
    company: raw.PAPP_NOM_PER,
    damageNature: raw.TYP_SIN_LIB,
    thirdParty: raw.TIERS ?? '-',
    description: raw.DESC ?? '-',
  };
}

export function TaskDetailPage() {
  const { taskId: taskIdParam, tenantSlug } = useParams<{ taskId?: string; tenantSlug: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // The task comes from the route (`/task/:taskId`) or from `?task=`. None ⇒
  // TASK-LESS claim screen (`/task?id_sin=…`, opened from the claim search —
  // mahhp 22861): no task info fetched, `task_type` is "AUTRE" in the rule
  // context, the title names the claim, and "Clôturer la tâche" is hidden.
  const taskIdQuery = searchParams.get('task');
  const taskId =
    taskIdParam ?? (taskIdQuery === null || taskIdQuery === '' ? undefined : taskIdQuery);
  const state = location.state as { ses?: string; id_sin?: string } | null;
  const storedSessionId = useClaimFlowAuthStore((s) => s.spoofedSessionId);
  const authUser = useClaimFlowAuthStore((s) => s.user);
  const ses = state?.ses ?? searchParams.get('ses') ?? (storedSessionId != null ? String(storedSessionId) : null);
  const idSin = state?.id_sin ?? searchParams.get('id_sin');
  const resetAiChat = useAiChatStore((s) => s.reset);

  const [claimSummary, setClaimSummary] = useState<ClaimSummary | null>(null);
  const [costs, setCosts] = useState<CostLine[]>([]);
  const [totalCost, setTotalCost] = useState(0);
  const [guaranteesToOpen, setGuaranteesToOpen] = useState<VeosGuaranteeToOpen[]>([]);
  const [guaranteesToClose, setGuaranteesToClose] = useState<VeosGuaranteeToClose[]>([]);
  const [taskInfo, setTaskInfo] = useState<VeosTaskInfo | null>(null);
  const [ncbtSin, setNcbtSin] = useState<string | null>(null);
  // Provisions "à encaisser" — fetched here (not only inside the RecoveryCard)
  // so a `hide` rule can be evaluated against their count/total *before* the
  // card mounts. `null` = the fetch failed; the rule variables then stay
  // `undefined` and the card fails open.
  const [provisionsToRecover, setProvisionsToRecover] = useState<
    ProvisionToRecoverRow[] | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [estimationRefreshKey, setEstimationRefreshKey] = useState(0);

  const refetch = useCallback(async () => {
    if (!ses || !idSin) {
      setError('Paramètres manquants : ses et id_sin sont requis.');
      setLoading(false);
      return;
    }

    try {
      setError(null);

      const [
        rawClaimSummary,
        erpLines,
        openableGuarantees,
        closableGuarantees,
        rawTaskInfo,
        recoverableProvisions,
      ] = await Promise.all([
        taskService.fetchClaimSummary(Number(ses), Number(idSin)),
        taskService.fetchCosts(Number(ses), Number(idSin)),
        taskService.fetchGuaranteesToOpen(Number(ses), Number(idSin)),
        taskService.fetchGuaranteesToClose(Number(ses), Number(idSin)),
        // The task by its id (WS2_GS_SEL_TACHE, mahhp 22943) — found even when
        // closed or assigned to someone else. Fail-soft: `null` = unknown.
        taskId ? taskService.fetchTaskInfo(taskId).catch(() => null) : Promise.resolve(null),
        // Rule-context only: a failure must never fail the page (nor set
        // `error`) — it degrades to `null`, i.e. "unknown", so any rule keyed
        // on the count/total simply does not fire.
        taskService.fetchProvisionsToRecover(Number(ses), Number(idSin)).catch(() => null),
      ]);

      setClaimSummary(mapClaimSummary(rawClaimSummary));
      setNcbtSin(rawClaimSummary.NCBT_SIN);

      const mapped = mapCostLines(erpLines);
      setCosts(mapped);
      // Claim total = the garanties' main rows only (poste rows break them
      // down) — feeds the card header/footer AND the rule context `total_cost`.
      setTotalCost(
        erpLines
          .filter((l) => isGuaranteeMainRow(l.CD_PSD))
          .reduce((acc, l) => acc + l.COUT_CIE, 0),
      );
      setGuaranteesToOpen(openableGuarantees);
      setGuaranteesToClose(closableGuarantees);
      setTaskInfo(rawTaskInfo);
      setProvisionsToRecover(recoverableProvisions);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Erreur lors du chargement des données.',
      );
    } finally {
      setLoading(false);
    }
  }, [ses, idSin, taskId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  // Entering a claim detail view always starts a fresh AI chat thread.
  //
  // `useAiChatStore` is a module-level singleton with no claim dimension, so
  // without this the previous claim's transcript stays in the drawer *and* is
  // replayed as request history on the next send — shipping claim A's
  // conversation to the LLM under claim B's system prompt. Always-fresh was
  // chosen over a per-claim thread map: data hygiene beats convenience, so
  // re-entering the *same* claim clears too.
  //
  // The page (not `AIChat`) drives it because `AIChat` renders conditionally
  // and, on a params-only `/task/A` -> `/task/B` navigation, never unmounts.
  // `reset()` also aborts the in-flight stream, so claim A's tokens can never
  // land in claim B's thread. Re-running under StrictMode's double-invoked
  // effects is harmless — clearing an already-empty store is a no-op.
  useEffect(() => {
    resetAiChat();
  }, [ses, idSin, resetAiChat]);

  // "Autres actions" launcher: focus an already-rendered card or inject one
  // just above the panel, then scroll/highlight it. The cards self-anchor on
  // their own root, so the page never wraps them. Ephemeral (resets on reload)
  // — see `useActionInjection`.
  const { injectedKeys, selectAction } = useActionInjection();

  // "Répondre" clicked in the Documents POPOUT window (mahhp 22227): the popout
  // has no Email card, so it posts the request on the popout channel and this
  // claim view hands it to its own Email card through the claim-data store —
  // exactly what a click in this window's Documents card does. Listening here
  // (the claim view, mounted for its whole lifetime — loading included — and
  // only ever in the main window) rather than in the main-window DocumentsCard
  // keeps the relay alive even when an admin rule hides the Documents card, and
  // drops the message on pages without an Email card (e.g. the dashboard)
  // instead of leaving a stale request behind. The Email card checks the
  // request's claim id, so a popout gone stale on another claim is ignored.
  const requestEmailReply = useClaimDataStore((s) => s.requestEmailReply);
  usePopoutEmailReplyListener(requestEmailReply);

  // Visible launcher rows (declarative config → stable keys), in mockup order.
  const visibleActions = useMemo(
    () =>
      [...OTHER_ACTIONS]
        .sort((a, b) => a.order - b.order)
        .filter((action) => action.visible)
        .map((action) => ({ key: action.key, label: action.label })),
    [],
  );

  // Rule context — see `../contextVariables.ts` for the schema + factory.
  // `tenant_slug` is sourced from the `:tenantSlug` route param mounted by
  // `App.tsx` (`/claimflow/:tenantSlug/*`); it powers per-tenant `setProp`
  // rules on the SettlementCard (e.g. `iga-servicing` defaults).
  const ruleContextValue = useMemo(
    () =>
      buildTaskDetailRuleContext({
        taskInfo,
        claimSummary,
        ncbtSin,
        totalCost,
        provisionsToRecover,
        tenantSlug,
        taskless: taskId === undefined,
      }),
    [taskInfo, claimSummary, ncbtSin, totalCost, provisionsToRecover, tenantSlug, taskId],
  );

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 flex items-center justify-center min-h-[200px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Alert variant="error" title="Erreur">
          <Text size="sm">{error}</Text>
        </Alert>
      </div>
    );
  }

  // Backing card for an injected action key, with the exact props the page
  // already passes that card in its always-on slot. The card self-anchors on
  // its root, so no wrapper is needed. Coverage cards are injected with the
  // (possibly empty) guarantee arrays — verified to render gracefully with
  // `guarantees={[]}` (empty checkbox group, no crash).
  const renderInjected = (key: string): ReactNode => {
    switch (key) {
      case 'ouvertureSinistre':
        return ses ? <OuvertureSinistreCard ses={ses} idSin={idSin ?? undefined} /> : null;
      case 'nouvelleProvision':
        return ses && idSin ? (
          <EstimationCard
            ses={ses}
            idSin={idSin}
            ncbtSin={ncbtSin ?? ''}
            guaranteesToClose={guaranteesToClose}
            onProvisionSaved={() => { void refetch(); }}
            refreshKey={estimationRefreshKey}
          />
        ) : null;
      case 'estimationRecours':
        return ses && idSin ? (
          <EstimationRecoursCard
            ses={ses}
            idSin={idSin}
            ncbtSin={ncbtSin ?? ''}
            guaranteesToClose={guaranteesToClose}
            onEstimationSaved={() => { void refetch(); }}
            refreshKey={estimationRefreshKey}
          />
        ) : null;
      case 'ajouterIntervenant':
        return ses && idSin ? <IntervenantForm ses={ses} idSin={idSin} /> : null;
      case 'modifierIntervenant':
        return ses && idSin ? <EditIntervenantCard ses={ses} idSin={idSin} /> : null;
      case 'ouvertureGaranties':
        return ses && idSin ? (
          <OpenCoverageCard
            ses={ses}
            idSin={idSin}
            guarantees={guaranteesToOpen}
            onCoverageOpened={() => { setEstimationRefreshKey((k) => k + 1); void refetch(); }}
          />
        ) : null;
      case 'clotureGarantie':
        return ses && idSin ? (
          <CloseCoverageCard
            ses={ses}
            idSin={idSin}
            guarantees={guaranteesToClose}
            onCoverageClosed={() => { void refetch(); }}
          />
        ) : null;
      case 'email':
        return ses && idSin ? (
          <EmailCard
            idSin={idSin}
            ses={ses}
            ncbtSin={ncbtSin ?? ''}
            dossierNumber={claimSummary?.dossierNumber}
            senderEmail={authUser?.email}
            senderName={authUser?.name}
          />
        ) : null;
      case 'miseAJourDossier':
        return ses && idSin ? <MiseAJourDossierCard ses={ses} idSin={idSin} /> : null;
      case 'clotureSinistre':
        return ses && idSin ? (
          <ClotureSinistreCard idSin={idSin} onClaimClosed={() => { void refetch(); }} />
        ) : null;
      case 'creerTache':
        return ses && idSin ? <CreateTaskCard ses={ses} idSin={idSin} ncbtSin={ncbtSin ?? ''} /> : null;
      case 'editerDoc':
        return ses && idSin ? <EditDocumentCard idSin={idSin} /> : null;
      case 'reglement':
        return ses && idSin ? (
          <SettlementCard ses={ses} idSin={idSin} onSettlementCreated={() => { void refetch(); }} />
        ) : null;
      case 'encaisserRecours':
        return ses && idSin ? (
          <RecoveryCard ses={ses} idSin={idSin} onRecoveryRecorded={() => { void refetch(); }} />
        ) : null;
      case 'piecesMqDemandees':
        return ses && idSin ? <RequestPiecesCard idSin={idSin} /> : null;
      case 'piecesMqRecues':
        return ses && idSin ? <MissingPiecesCard idSin={idSin} /> : null;
      default:
        return null;
    }
  };

  // Legacy single-line title — the two-line `TaskTitle` (mahhp 22852) falls
  // back to it while its own fetch is pending, on error, or without a task:
  // "Tâche {id} — {type} — {classement}" on a task, "Dossier {NCBT_NCIE}" on a
  // task-less claim screen (mahhp 22861).
  const currentTitle = buildPageTitle(taskId, taskInfo, claimSummary?.dossierNumber);

  return (
    <RuleContextProvider value={ruleContextValue}>
      <div className="grid grid-cols-1 lg:grid-cols-9 gap-6 h-[calc(100vh-3.5rem)] p-6">
        {/* Left column — 6/9 width, independent scroll */}
        <div className="lg:col-span-6 overflow-y-auto">
          <TaskTitle taskId={taskId} idSin={idSin ?? undefined} fallback={currentTitle} />
          <VStack gap={6}>
            {claimSummary && <ClaimSummaryCard claim={claimSummary} />}
            <CostSynthesisCard costs={costs} totalCost={totalCost} />
            {ses && idSin && <ComptabiliteCard idSin={idSin} ses={ses} />}
            {ses && idSin && <ClaimTasksCard idSin={idSin} taskId={taskId} ses={ses} />}
            {ses && idSin && <IntervenantsCard idSin={idSin} ses={ses} />}
            {ses && idSin && (
              <DocumentsCard
                ses={ses}
                idSin={idSin}
                idEvent={taskId}
                dossierNumber={claimSummary?.dossierNumber}
                contractRef={ncbtSin ?? undefined}
                client={claimSummary?.client}
              />
            )}
          </VStack>
        </div>
        {/* Right column — 3/9 width, independent scroll. Cards render exactly as
            before; each self-anchors on its own root (no wrapper divs). The
            on-demand injected cards and the launcher panel sit at the bottom. */}
        <div className="lg:col-span-3 overflow-y-auto">
          <VStack gap={6}>
            {ses && idSin && (
              <IntervenantForm ses={ses} idSin={idSin} />
            )}
            {ses && idSin && (
              <EditIntervenantCard ses={ses} idSin={idSin} />
            )}
            {ses && idSin && (
              <EmailCard
                idSin={idSin}
                ses={ses}
                ncbtSin={ncbtSin ?? ''}
                dossierNumber={claimSummary?.dossierNumber}
                senderEmail={authUser?.email}
                senderName={authUser?.name}
              />
            )}
            {ses && idSin && guaranteesToOpen.length > 0 && (
              <OpenCoverageCard
                ses={ses}
                idSin={idSin}
                guarantees={guaranteesToOpen}
                onCoverageOpened={() => { setEstimationRefreshKey((k) => k + 1); void refetch(); }}
              />
            )}
            {ses && idSin && guaranteesToClose.length > 0 && (
              <CloseCoverageCard
                ses={ses}
                idSin={idSin}
                guarantees={guaranteesToClose}
                onCoverageClosed={() => { void refetch(); }}
              />
            )}
            {ses && idSin && (
              <RequestPiecesCard idSin={idSin} />
            )}
            {ses && idSin && (
              <MissingPiecesCard idSin={idSin} />
            )}
            {ses && idSin && (
              <SettlementCard
                ses={ses}
                idSin={idSin}
                onSettlementCreated={() => { void refetch(); }}
              />
            )}
            {ses && idSin && (
              <RecoveryCard
                ses={ses}
                idSin={idSin}
                onRecoveryRecorded={() => { void refetch(); }}
              />
            )}
            {ses && idSin && (
              <EstimationCard
                ses={ses}
                idSin={idSin}
                ncbtSin={ncbtSin ?? ''}
                guaranteesToClose={guaranteesToClose}
                onProvisionSaved={() => { void refetch(); }}
                refreshKey={estimationRefreshKey}
              />
            )}
            {ses && idSin && (
              <EstimationRecoursCard
                ses={ses}
                idSin={idSin}
                ncbtSin={ncbtSin ?? ''}
                guaranteesToClose={guaranteesToClose}
                onEstimationSaved={() => { void refetch(); }}
                refreshKey={estimationRefreshKey}
              />
            )}
            {ses && idSin && (
              <CreateTaskCard ses={ses} idSin={idSin} ncbtSin={ncbtSin ?? ''} />
            )}
            {taskId && (
              <CloseTaskCard
                taskId={taskId}
                taskInfo={taskInfo}
                idSin={idSin ?? undefined}
              />
            )}
            {ses && (
              <OuvertureSinistreCard ses={ses} idSin={idSin ?? undefined} />
            )}
            {ses && idSin && (
              <MiseAJourDossierCard ses={ses} idSin={idSin} />
            )}
            {/* « Clôture » (mahhp 22846): closing the claim changes its status,
                so the page re-fetches the claim summary (Résumé `LIB_MVT`). */}
            {ses && idSin && (
              <ClotureSinistreCard
                idSin={idSin}
                onClaimClosed={() => { void refetch(); }}
              />
            )}
            {ses && idSin && (
              <EditDocumentCard idSin={idSin} />
            )}
            {/* On-demand injected cards (not part of the always-on set). They are
                the same components, so they self-anchor — no wrapper divs. */}
            {injectedKeys.map((key) => {
              // Force-reveal an injected card even if an admin `hide` rule
              // would otherwise return null from `withComponentRules`, so the
              // launcher can scroll to it. No-op for non-hidden cards, all
              // other rule effects still apply.
              const el = renderInjected(key);
              return (
                <Fragment key={key}>
                  {isValidElement(el)
                    ? cloneElement(el, {
                        __forceVisible: true,
                      } as Partial<{ __forceVisible: boolean }>)
                    : el}
                </Fragment>
              );
            })}
            <OtherActionsPanel actions={visibleActions} onSelect={selectAction} />
          </VStack>
        </div>
      </div>
      {ses && idSin && (
        <CommentsPanel ses={ses} idSin={idSin} ncbtSin={ncbtSin ?? ''} />
      )}
      {ses && idSin && (
        <AIChat ses={ses} idSin={idSin} />
      )}
    </RuleContextProvider>
  );
}
